'use client'
import { createContext, useState, useCallback, useRef, useMemo } from 'react'
import type { FC, ReactNode } from 'react'

interface ProductState {
  isActiveProduct: boolean
  productSlug: string | null
  openProduct: (slug: string) => void
  closeProduct: () => void
}

const initialProductState: ProductState = {
  isActiveProduct: false,
  productSlug: null,
  openProduct: () => {},
  closeProduct: () => {},
}

export const ProductContext = createContext<ProductState>(initialProductState)

interface ProductProviderProps {
  children: ReactNode
}

export const ProductProvider: FC<ProductProviderProps> = ({ children }) => {
  const [isActiveProduct, setIsActiveProduct] = useState(false)
  const [productSlug, setProductSlug] = useState<string | null>(null)
  const previousPathRef = useRef<string | null>(null)

  const openProduct = useCallback((slug: string) => {
    const currentPathname = window.location.pathname
    if (!currentPathname.includes('/product/')) {
      previousPathRef.current = `${currentPathname}${window.location.search}`
    }
    setProductSlug(slug)
    setIsActiveProduct(true)
  }, [])

  const closeProduct = useCallback(() => {
    const currentPathname = window.location.pathname
    setIsActiveProduct(false)
    setProductSlug(null)
    if (currentPathname.includes('/product/') && previousPathRef.current) {
      previousPathRef.current = null
      window.history.back()
    }
  }, [])

  const contextValue = useMemo(() => ({ isActiveProduct, productSlug, openProduct, closeProduct }), [isActiveProduct, productSlug, openProduct, closeProduct ])

  return (
    <ProductContext.Provider value={contextValue}>
      {children}
    </ProductContext.Provider>
  )
}
